/*
 * Copyright (c) 2020.
 * This file is part of the Compo B2B application.
 * (c) Compo soft - Vladimir Ganturin <gun2rin@gmail.com>
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import {ChangeDetectionStrategy, Component, OnInit,} from '@angular/core';
import {NavigationEnd, ParamMap} from '@angular/router';
import {NbWindowService} from '@nebular/theme';
import {Catalog, CatalogWithAncestors, FileService, LazyCatalog} from 'compob2b-libs';
import {EMPTY, Observable} from 'rxjs';
import {filter, map, take, takeUntil} from 'rxjs/operators';
import {translations} from '../../../../../environments/translations.ru';
import {ListComponent} from '../../../../admin-shared/crud-components/list/list.component';
import {CrudConfig} from '../../../../admin-shared/interfaces/crud.config';
import {FormConfigService} from '../../../../admin-shared/services/form-config.service';
import {CATALOG_FORM} from '../catalog-forms.config';
import {CatalogService} from '../catalog.service';

@Component({
  selector: 'app-catalog-tree',
  templateUrl: './catalog-lazy.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CatalogLazyComponent extends ListComponent<Catalog> implements OnInit {
  isOpened = true;
  catalogId = 0;
  catalog: LazyCatalog[];
  reindexing = false;
  itemPage = false;
  config: CrudConfig = {
    uri: '/catalog',
    columns: CATALOG_FORM,
    defaultSortColumn: 'header',
    defaultSortDirection: 'asc',
    title: translations.catalog,
  };
  data1: LazyCatalog[] = [];

  constructor(protected readonly formsConfig: FormConfigService,
              protected readonly windowService: NbWindowService,
              protected readonly catalogService: CatalogService,
              protected readonly fileService: FileService
  ) {
    super(formsConfig, windowService, fileService);
  }

  filterData(): Observable<Catalog[]> {
    return EMPTY;
  }

  initCatalogTree(): void {
    this.catalogService.initCatalogTree(this.catalogId, this.showDeleted)
      .pipe(take(1))
      .subscribe(tree => {
        if (!this.catalogId) {
          this.data1 = tree;
        } else {
          const listOfRoot = tree.filter(item => item.parentId == 1);
          this.data1 = listOfRoot;
          this.treeRecursion(tree, listOfRoot);
        }
        this.cdr.detectChanges();
      });
  }

  addChildrenToParentByParentId(id, node, childNodes) {
    let isSuccess = false;

    const {children} = node;
    if (node.id == id) {
      childNodes = [].concat(childNodes);
      node.children =
        ((Array.isArray(children) && children) || []).filter(child =>
          childNodes.every(node => node.id !== child.id)
        ).concat(childNodes);
      isSuccess = true;
    } else if (Array.isArray(children)) {
      isSuccess = children.some(child =>
        this.addChildrenToParentByParentId(id, child, childNodes)
      );
    }
    return isSuccess;
  }

  treeRecursion(tree, roots) {
    roots.forEach(item => {
      const childs = tree.filter(child => child.parentId == item.id);
      if (childs.length) {
        item.expanded = true;
        this.addChildrenToParentByParentId(item.id, item, childs);
        this.treeRecursion(tree, childs);
      }
    });
    this.cdr.detectChanges();
  }

  toggleOpened() {
    this.isOpened = !this.isOpened;
  }

  ngOnInit() {
    super.setConfig(this.config);
    super.ngOnInit();
    this.trackItemPage();

    this.route.queryParamMap
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(params => {
        this.loadByParams(params);
      });
  }

  getParent(root, id) {
    let nodes;
    root.some((n) => {
      if (n.id == id) {
        return nodes = n;
      }
      if (n.children) {
        return nodes = this.getParent(n.children, id);
      }
    });
    return nodes || null;
  }

  loadByParams(params: ParamMap): void {
    this.catalogId = Number(params.get('catalog_id'));

    if (!this.catalogId) {
      this.data1.forEach(item => {
        const index = this.data1.indexOf(item);
        item.expanded = false;
        delete item.children;
        this.data1[index] = item;
      });

      this.initCatalogTree();
    } else {
      if (!this.getParent(this.data1, this.catalogId)) {
        this.initCatalogTree();
      }
    }

    this.cdr.detectChanges();
  }

  toggleDeleted(): void {
    this.showDeleted = !this.showDeleted;
    this.initCatalogTree();
    this.cdr.detectChanges();
  }

  setId(itemId: number, navigate: boolean) {
    const item = this.getParent(this.data1, itemId);
    this.catalogId = itemId;

    if (!item) {
      this.navigateToId(this.catalogId);

      return true;
    }
    item.expanded = !item.expanded;

    if (item.children === undefined && item.lastLevel === false) {
      this.catalogService.getLazyChildren(item.id, this.showDeleted)
        .pipe(take(1))
        .subscribe(dataChild => {
          this.addChildrenToParentByParentId(item.id, item, dataChild);
          this.cdr.detectChanges();
        });
    }
    this.cdr.detectChanges();

    if (navigate) {
      this.navigateToId(this.catalogId);
    }
  }

  navigateToId(id: number) {
    if (!id) {
      this.router.navigate(['cabinet', 'pim', 'catalog']);
    } else {
      this.router.navigate(['cabinet', 'pim', 'catalog', id, 'products'], {queryParams: {catalog_id: this.catalogId}});
    }
  }

  afterDelete(id) {
    const catalogToDelete = this.getParent(this.data1, id);
    if (catalogToDelete && catalogToDelete.parentId) {
      const parentCatalog = this.getParent(this.data1, catalogToDelete.parentId);
      if (parentCatalog === null) {
        this.data1 = this.data1.filter(item => item.id !== id);
      } else {
        parentCatalog.children = parentCatalog.children.filter(item => item.id !== id);
      }

      this.successToast(translations.catalogRemoved);

      if (this.catalogId == id) {
        if (catalogToDelete.parentId && catalogToDelete.parentId !== 1) {
          this.navigateToId(catalogToDelete.parentId);
        } else {
          this.navigateToId(0);
        }
      }
    }

    this.cdr.detectChanges();
  }

  onCatalogSelected($event: CatalogWithAncestors) {
    this.setId($event.id, true);
  }

  onResize(width: string) {
    localStorage.setItem('CatalogLazyWidth', parseInt(width).toString());
  }

  reindex() {
    if (this.reindexing) {
      return;
    }
    this.reindexing = true;
    this.loading = true;
    return this.compoApiService.get<any>('/catalog/reindex')
      .pipe(
        map(response => response.data),
        takeUntil(this.unsubscribe)
      ).subscribe(_ => {
        console.log('reindex');
        this.initCatalogTree();
        this.successToast('Процесс переиндексации каталогов завершён');
        this.reindexing = false;
        this.loading = false;
      });
  }

  exportCatalogStructure(): void {
    this.loading = true;
    this.catalogService.exportStructure()
      .pipe(
        take(1),
        takeUntil(this.unsubscribe)
      )
      .subscribe(response => {
        this.handleDownloadResponse(response, 'catalog-structure.xlsx');
      }, _ => {
        this.loading = false;
      });
  }

  get width(): number {
    let width = Number(localStorage.getItem('CatalogLazyWidth'));
    return width > 10 ? width : 310;
  }

  trackItemPage() {
    this.itemPage = this.router.url.indexOf('/item/') > -1;
    this.router.events.pipe(
      takeUntil(this.unsubscribe),
      filter(event => event instanceof NavigationEnd),
    ).subscribe((event: NavigationEnd) => {
      this.itemPage = event.url.indexOf('/item/') > -1;
    });
  }

}
